// thumbnail/core.ts — the single front-end thumbnail builder.
//
// This is the JS/TS replacement for the PHP get_thumbnail() (includes/functions/get_thumbnail_inc.php).
// The compiled item arr already carries its full `pictures` bucket (arr.pictures[<type>][i].picture_url),
// so a PHP emitter (ft_thumbnail_mount) ships that data verbatim as a JSON blob and ALL the work happens
// here: pick the display URL from the pictures bucket, paint the <img>/initials badge, and overlay the
// creature-number + inventory/creature badges. No server-side thumbnail HTML, and NO database ping.
//
// Markup parity: buildThumbnailNode() reproduces generate_thumbnail_html() /
// generate_default_thumbnail_html() + the position-relative wrapper and badge overlays from
// get_thumbnail_inc.php so the rendered thumbnail looks identical to the legacy raw-HTML cell.
//
// Originally lived at includes/js-src/catalog-list/thumbnail.ts (catalog-table pipeline only); promoted
// here as a standalone bundle (window.FtThumbnail + window.ftMountThumbnails) so every former
// get_thumbnail() call site can mount from array data.

// iconMap is a runtime global from includes/js/core-helpers.js (loaded in the footer before the
// bundles). Declared ambient in globals.d.ts. We render badge icons client-side via iconMap instead of
// baking icon_map() HTML in PHP, keeping the payload pure data.

// One picture row and the bucket, exactly as PHP ships them (arr.pictures). Fields are optional because
// the compile elides defaults (e.g. picture_thumbnail/primary_picture omitted when 0).
export interface PictureEntry {
    picture_url?: string;
    pic_url?: string; // legacy alias still tolerated (pic_field() resolves either to picture_url)
    primary_picture?: number;
    picture_thumbnail?: number;
}
export type PicturesBucket = Record<string, PictureEntry[]>;

// One target status effect, narrowed to what the shapeshift walk needs. The emitter ships these (from
// creature_statuses($arr,'target')) only when the row is shapeshifted; TS does the DETECTION — find the
// first creature-typed status effect that carries a picture and use it (parity with get_thumbnail()'s
// shapeshift branch, lines 133-151).
export interface ThumbnailStatusEffect {
    itemType?: string | null;
    pictureUrl?: string | null;
}

// Inventory/creature overlay badges. The emitter passes booleans + qty (read from the arr); TS renders
// each via the global iconMap(). Mirrors get_thumbnail_inc.php lines 340-425.
export interface ThumbnailBadges {
    // bottom-right
    unidentified?: boolean;
    inventoryPrivate?: boolean;
    qty?: number | null; // qty badge only when > 1
    encounterCreaturePrivate?: boolean;
    controlled?: boolean;
    // top-right (inventory only)
    encounterLink?: boolean; // inventory_encounter_link
    immovable?: boolean;
    integrated?: boolean; // integrated_object
    locked?: boolean;
}

// The pure-data payload a thumbnail mount carries.
export interface ThumbnailData {
    pictures?: PicturesBucket | null; // arr.pictures (flattened: top-level + EC/PC nested), passthrough
    item_id?: string;
    name?: string;
    link?: boolean; // true → clickable (openItemInfo) + cursor-pointer
    w?: number; // box width in px (default 60)
    h?: number | null; // box height in px; null → width-only (height auto), matching get_thumbnail()
    creature_number?: number | null; // bottom-left count badge (> 0), else omitted
    imgClasses?: string; // <img> class(es); default 'img-thumbnail' (e.g. 'img-thumbnail d-block w-100')
    // Forced-type selector — the array-data analogue of get_thumbnail()'s attached_item_type /
    // picture_type_request. When set, selection is RESTRICTED to that selector's ordered type list (and
    // falls back to initials if none match), exactly like the gated location_map / location_picture / XZ
    // branches. When null/unset, the default priority cascade runs.
    selector?: ThumbnailSelector | null;
    directUrl?: string | null; // denormalized roll snapshot (arr.pic_url) — wins over the cascade
    size?: string | null; // raw creature-size UUID off the data shape; TS derives the full-quality PC pick
    shapeshifted?: boolean; // when true, the shapeshift walk over statusEffects runs first
    statusEffects?: ThumbnailStatusEffect[] | null; // target status effects, for the shapeshift walk
    badges?: ThumbnailBadges | null;
}

// Default priority cascade — mirrors get_thumbnail()'s elseif chain with no picture_type_request:
// inventory → encounter-creature → player-character → location map → location → encounter → base item →
// classification thumbnail. First type with a usable URL wins. XZ / thumbnail-variant types are NOT in
// the cascade — they only resolve through an explicit `selector` (just as get_thumbnail() resolves them
// only on a picture_type_request).
const PICTURE_TYPE_PRIORITY: readonly string[] = [
    'inventory_picture',
    'encounter_creature_picture',
    'player_character_picture',
    'location_map',
    'location_picture',
    'encounter_picture',
    'item',
    // Classification-thumbnail fallback (get_thumbnail()'s primary_picture_of() tail): an item with no
    // picture of its own borrows its classification's art. After 'item' so an item's OWN picture wins.
    'weapon_classification',
    'weapon_ammo_classification',
    'armor_classification',
    'instrument_classification',
    'armor_type',
];

// Forced-type selectors and the ordered type list each resolves from (gated selection). Mirrors the
// picture_type_request / attached_item_type branches of get_thumbnail():
//   - inventory_xz: instance inventory_picture beats template-hydrated inventory_presentation_picture (lines 186-200)
//   - the rest map 1:1 to a single bucket.
export type ThumbnailSelector =
    | 'location_map'
    | 'location_picture'
    | 'encounter_picture'
    | 'inventory_xz'
    | 'encounter_creature_xz'
    | 'encounter_creature_thumbnail'
    | 'inventory_thumbnail'
    | 'source_header'
    | 'user_picture';

const SELECTOR_TYPES: Record<ThumbnailSelector, readonly string[]> = {
    location_map: ['location_map'],
    location_picture: ['location_picture'],
    encounter_picture: ['encounter_picture'],
    inventory_xz: ['inventory_picture', 'inventory_presentation_picture'],
    encounter_creature_xz: ['encounter_creature_presentation_picture'],
    encounter_creature_thumbnail: ['encounter_creature_thumbnail_picture'],
    inventory_thumbnail: ['inventory_thumbnail_picture'],
    // Source cover art — the header gallery the upload pipeline stores under
    // pictures.source_header / source_header_picture (build_source_arr.php). Gated like the location
    // selectors so a source row paints its cover (or initials) without the default item cascade.
    source_header: ['source_header', 'source_header_picture'],
    // User profile avatar (build_user_arr's user_picture bucket). Gated so a user row paints its
    // avatar (or initials) without the item cascade — the GM card on campaign.php.
    user_picture: ['user_picture'],
};

// Read a picture entry's URL, tolerating the legacy pic_url alias (mirrors pic_field('picture_url')).
function entryUrl(entry: PictureEntry | null | undefined): string | null {
    if (!entry) return null;
    const url = entry.picture_url ?? entry.pic_url;
    return typeof url === 'string' && url !== '' ? url : null;
}

// Within one type's list, prefer the entry flagged primary_picture===1, else the first usable URL.
function urlFromList(list: PictureEntry[] | null | undefined): string | null {
    if (!Array.isArray(list) || list.length === 0) return null;
    for (const entry of list) {
        if (entry && entry.primary_picture === 1) {
            const url = entryUrl(entry);
            if (url !== null) return url;
        }
    }
    for (const entry of list) {
        const url = entryUrl(entry);
        if (url !== null) return url;
    }
    return null;
}

// Creature-size UUIDs. PHP ships the raw `size` value off the data shape; the "larger than medium"
// decision (which drives the full-quality PC-art preference) is made HERE in TS, not in PHP.
const SIZE_TINY = '13456FEC-A2EE-DB46-5829-0EB2F627E1B7';
const SIZE_SMALL = 'AAC1F466-7A8B-C593-1ADC-FC402B8909D7';
const SIZE_MEDIUM = 'DC23AA88-CB3D-EA4E-F6F7-6389ABC4F9DC';
function isLargerThanMedium(size: string | null | undefined): boolean {
    return typeof size === 'string' && size !== '' && size !== SIZE_TINY && size !== SIZE_SMALL && size !== SIZE_MEDIUM;
}

// PC-picture pick with the size rule: a larger-than-medium creature prefers a full-quality (non-thumbnail)
// picture if one exists, else falls back to the normal within-type pick. Ports get_thumbnail() lines 212-238.
function pcPictureUrl(list: PictureEntry[] | null | undefined, isLargeCreature: boolean): string | null {
    if (isLargeCreature && Array.isArray(list)) {
        for (const entry of list) {
            if (entry && (entry.picture_thumbnail ?? 1) === 0) {
                const url = entryUrl(entry);
                if (url !== null) return url;
            }
        }
    }
    return urlFromList(list);
}

// The shapeshift walk: first creature-typed target status effect that carries a picture wins. Ports
// get_thumbnail()'s shapeshift branch (the data — which status — is decided HERE, not in PHP).
function shapeshiftUrl(effects: ThumbnailStatusEffect[] | null | undefined): string | null {
    if (!Array.isArray(effects)) return null;
    for (const eff of effects) {
        if (eff && eff.itemType === 'creature' && typeof eff.pictureUrl === 'string' && eff.pictureUrl !== '') {
            return eff.pictureUrl;
        }
    }
    return null;
}

// Pick the display URL from the data. Order: shapeshift (if flagged) → forced selector (gated) → default
// priority cascade. Returns null when nothing matched (→ caller draws the initials badge).
export function selectThumbnailUrl(data: ThumbnailData): string | null {
    if (data.shapeshifted) {
        const ss = shapeshiftUrl(data.statusEffects);
        if (ss !== null) return ss;
    }

    const pictures = data.pictures;

    // Forced selector is gated even when there's no bucket — a selector that finds nothing → initials.
    if (data.selector && SELECTOR_TYPES[data.selector]) {
        if (!pictures || typeof pictures !== 'object') return null;
        for (const type of SELECTOR_TYPES[data.selector]) {
            const url = urlFromList(pictures[type]);
            if (url !== null) return url;
        }
        return null; // gated: never falls through to the cascade
    }

    // Denormalized roll snapshot (arr.pic_url) — get_thumbnail()'s pic_url branch; beats the cascade.
    // Checked before the pictures-bucket guard since a roll arr may carry only pic_url, no bucket.
    if (typeof data.directUrl === 'string' && data.directUrl !== '') return data.directUrl;

    if (!pictures || typeof pictures !== 'object') return null;

    for (const type of PICTURE_TYPE_PRIORITY) {
        if (type === 'player_character_picture') {
            const url = pcPictureUrl(pictures[type], isLargerThanMedium(data.size));
            if (url !== null) return url;
            continue;
        }
        const url = urlFromList(pictures[type]);
        if (url !== null) return url;
    }
    return null;
}

// First two letters of the name, first capitalized + second lowercase (e.g. "Av", "Ba") regardless of the
// stored casing — identical to get_thumbnail()'s ucfirst(strtolower(substr($name, 0, 2))). The two-letter
// rule is the fix for the old single-letter "A" descalar bug.
function initialsOf(name: string): string {
    const two = (name || '').slice(0, 2).toLowerCase();
    return two ? two.charAt(0).toUpperCase() + two.slice(1) : '';
}

// Build the <img> for a resolved URL — ports generate_thumbnail_html(). h present → an aspect-fit flex box
// (full image, no crop, scaled to fit w×h); h null → width-only with height auto (the PC-list case).
function buildImageNode(url: string, w: number, h: number | null, alt: string, imgClasses: string): HTMLElement {
    if (h !== null) {
        const box = document.createElement('div');
        box.style.cssText =
            `width:${w}px;height:${h}px;display:flex;align-items:center;justify-content:center;overflow:hidden;`;
        const img = document.createElement('img');
        img.className = imgClasses;
        img.style.cssText = 'max-width:100%;max-height:100%;width:auto;height:auto;';
        img.src = url;
        img.alt = alt;
        box.appendChild(img);
        return box;
    }
    const box = document.createElement('div');
    const img = document.createElement('img');
    img.className = imgClasses;
    img.style.cssText = `width:${w}px;height:auto;`;
    img.src = url;
    img.alt = alt;
    box.appendChild(img);
    return box;
}

// Build the yellow initials badge — ports generate_default_thumbnail_html(). The badge box is square (w×w),
// matching get_thumbnail()'s call (width passed for both width and height).
function buildInitialsNode(initials: string, w: number): HTMLElement {
    const box = document.createElement('div');
    box.style.cssText = `height:${w}px;width:${w}px;`;
    box.className = 'bg-warning border border-dark rounded d-flex justify-content-center align-items-center';
    const span = document.createElement('span');
    span.className = 'fw-bold h4 text-dark';
    span.textContent = initials;
    box.appendChild(span);
    return box;
}

// iconMap() returns trusted HTML (an <img>/<svg> string). Render it into a throwaway container and move
// the resulting nodes — keeps buildThumbnailNode all-DOM (no innerHTML on the live wrapper).
function appendIconHtml(target: HTMLElement, html: string): void {
    if (!html) return;
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    while (tmp.firstChild) target.appendChild(tmp.firstChild);
}

const hasIconMap = (): boolean => typeof iconMap === 'function';

// Bottom-right overlay: unidentified / private / qty (inventory) and encounter_creature_private /
// controlled (creature). Ports get_thumbnail_inc.php lines 340-379.
function buildBottomRightBadges(b: ThumbnailBadges): HTMLElement | null {
    const parts: string[] = [];
    if (hasIconMap()) {
        if (b.unidentified) parts.push(iconMap('unidentified'));
        if (b.inventoryPrivate) parts.push(iconMap('inventory_private'));
    }
    if (typeof b.qty === 'number' && b.qty > 1) {
        parts.push(`<span class="badge bg-light text-dark border">${b.qty}</span>`);
    }
    if (hasIconMap()) {
        if (b.encounterCreaturePrivate) parts.push(iconMap('encounter_creature_private'));
        if (b.controlled) parts.push(iconMap('controlled'));
    }
    if (parts.length === 0) return null;
    const holder = document.createElement('div');
    holder.className = 'position-absolute bottom-0 end-0 extra_icons_info';
    appendIconHtml(holder, parts.join(' '));
    return holder;
}

// Top-right overlay (inventory only): link / immovable / integrated / locked. Ports lines 382-425.
function buildTopRightBadges(b: ThumbnailBadges): HTMLElement | null {
    if (!hasIconMap()) return null;
    const parts: string[] = [];
    if (b.encounterLink) parts.push(iconMap('link', 15, 15));
    if (b.immovable) parts.push(iconMap('immovable', 20, 20));
    if (b.integrated) parts.push(iconMap('integrated_object', 20, 20));
    if (b.locked) parts.push(iconMap('locked'));
    if (parts.length === 0) return null;
    const holder = document.createElement('div');
    holder.className = 'position-absolute top-0 end-0 extra_icons_info';
    appendIconHtml(holder, parts.join(''));
    return holder;
}

// Build the full thumbnail node: <img>-or-initials inside the position-relative wrapper, with the same
// Bootstrap tooltip (instant, via template.js body delegation), optional openItemInfo click, and the
// creature-number + inventory/creature badge overlays the PHP wrapper carried. Everything is built via the
// DOM (no innerHTML on the live wrapper) so no markup can be injected.
export function buildThumbnailNode(data: ThumbnailData): HTMLElement {
    const w = data.w ?? 60;
    const h = data.h ?? null;
    const name = data.name ?? '';
    const itemId = data.item_id ?? '';
    const imgClasses = data.imgClasses ?? 'img-thumbnail';
    const linkable = data.link === true && itemId !== '';

    const wrapper = document.createElement('div');
    wrapper.className = linkable ? 'position-relative cursor-pointer' : 'position-relative';
    wrapper.setAttribute('data-bs-toggle', 'tooltip');
    wrapper.setAttribute('data-bs-original-title', name);
    wrapper.title = name; // a11y; template.js swaps to the instant Bootstrap tooltip on hover
    if (linkable) {
        wrapper.addEventListener('click', () => {
            if (typeof openItemInfo === 'function') openItemInfo(itemId);
            else console.warn('[ft-thumbnail] openItemInfo unavailable');
        });
    }

    const url = selectThumbnailUrl(data);
    wrapper.appendChild(url !== null ? buildImageNode(url, w, h, name, imgClasses) : buildInitialsNode(initialsOf(name), w));

    // Bottom-left creature-number badge (parity with get_thumbnail()'s non-inventory branch).
    if (typeof data.creature_number === 'number' && data.creature_number > 0) {
        const holder = document.createElement('div');
        holder.className = 'position-absolute bottom-0 start-0';
        const badge = document.createElement('span');
        badge.className = 'badge bg-dark text-light border';
        badge.textContent = String(data.creature_number);
        holder.appendChild(badge);
        wrapper.appendChild(holder);
    }

    if (data.badges) {
        const br = buildBottomRightBadges(data.badges);
        if (br) wrapper.appendChild(br);
        const tr = buildTopRightBadges(data.badges);
        if (tr) wrapper.appendChild(tr);
    }

    return wrapper;
}
